import { useEffect, useMemo, useRef, useState } from 'react'
import {
  parsePhoneNumberFromString,
  getCountries,
  getCountryCallingCode,
} from 'libphonenumber-js'

// Very small country list — the ones a Miami laundry actually sees.
// Everything else is still reachable via the "More…" option (full list).
const POPULAR = ['US', 'CA', 'MX', 'FR', 'GB', 'BR', 'CO', 'HT', 'DO', 'ES', 'IT', 'DE']

const FLAGS = {
  US: '🇺🇸', CA: '🇨🇦', MX: '🇲🇽', FR: '🇫🇷', GB: '🇬🇧',
  BR: '🇧🇷', CO: '🇨🇴', HT: '🇭🇹', DO: '🇩🇴', ES: '🇪🇸',
  IT: '🇮🇹', DE: '🇩🇪',
}

function flagFor(country) {
  if (FLAGS[country]) return FLAGS[country]
  // Regional indicator symbols fallback so any ISO2 renders as a flag.
  return String.fromCodePoint(
    ...country.toUpperCase().split('').map((c) => 0x1f1a5 + c.charCodeAt(0)),
  )
}

// Controlled phone input. `value` is the E.164 string ('+19549009969').
// `onChange` gets the E.164 whenever it becomes valid; when invalid we
// still call it with the raw digits so parent state reflects typing.
export default function PhoneInput({
  value = '',
  onChange,
  onValidityChange,
  defaultCountry = 'US',
  placeholder = 'Phone number',
  className = '',
  autoComplete = 'tel',
  required = false,
}) {
  const [country, setCountry] = useState(() => detectCountry(value, defaultCountry))
  const [national, setNational] = useState(() => extractNational(value, country))
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  // Close country picker on outside click.
  useEffect(() => {
    if (!open) return
    function onDown(e) {
      if (!wrapRef.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // Push valid E.164 up whenever it changes.
  useEffect(() => {
    const parsed = parsePhoneNumberFromString(national, country)
    if (parsed && parsed.isValid()) {
      onChange?.(parsed.number)
      onValidityChange?.(true)
    } else {
      onChange?.(national)
      onValidityChange?.(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [national, country])

  const dial = useMemo(() => `+${getCountryCallingCode(country)}`, [country])

  const options = useMemo(() => {
    const all = getCountries()
    const popular = POPULAR.filter((c) => all.includes(c))
    const rest = all.filter((c) => !popular.includes(c)).sort()
    return { popular, rest }
  }, [])

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <div className="flex items-stretch gap-0 rounded-lg border-[1.5px] border-line bg-white focus-within:border-navy">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1 rounded-l-lg border-r border-line px-2.5 text-[13px] font-bold text-navy"
          aria-label="Country code"
        >
          <span className="text-[16px] leading-none">{flagFor(country)}</span>
          <span className="text-[11px] text-ink-2">{dial}</span>
          <span aria-hidden className="text-[9px] text-ink-3">▾</span>
        </button>
        <input
          type="tel"
          inputMode="tel"
          autoComplete={autoComplete}
          placeholder={placeholder}
          required={required}
          value={national}
          onChange={(e) => setNational(e.target.value)}
          className="w-full rounded-r-lg bg-white px-3 py-2.5 text-[13px] text-ink placeholder:text-ink-3 focus:outline-none"
        />
      </div>

      {open && (
        <div className="absolute left-0 right-0 z-40 mt-1 max-h-[280px] overflow-y-auto rounded-lg border border-line bg-white shadow-lg">
          <div className="px-3 py-2 text-[9px] font-bold uppercase tracking-widest text-ink-3">
            Popular
          </div>
          {options.popular.map((c) => (
            <CountryRow key={c} code={c} current={country === c} onSelect={() => { setCountry(c); setOpen(false) }} />
          ))}
          <div className="mt-1 border-t border-line px-3 py-2 text-[9px] font-bold uppercase tracking-widest text-ink-3">
            More
          </div>
          {options.rest.map((c) => (
            <CountryRow key={c} code={c} current={country === c} onSelect={() => { setCountry(c); setOpen(false) }} />
          ))}
        </div>
      )}
    </div>
  )
}

function CountryRow({ code, current, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] ${
        current ? 'bg-navy/5 font-bold text-navy' : 'text-ink hover:bg-line/40'
      }`}
    >
      <span className="text-[16px] leading-none">{flagFor(code)}</span>
      <span className="flex-1">{code}</span>
      <span className="text-[11px] text-ink-2">+{getCountryCallingCode(code)}</span>
    </button>
  )
}

// ── Helpers ────────────────────────────────────────────────────────────

function detectCountry(e164, fallback) {
  if (!e164) return fallback
  try {
    const parsed = parsePhoneNumberFromString(e164)
    if (parsed?.country) return parsed.country
  } catch { /* ignore */ }
  return fallback
}

function extractNational(e164, country) {
  if (!e164) return ''
  try {
    const parsed = parsePhoneNumberFromString(e164, country)
    if (parsed?.nationalNumber) return parsed.nationalNumber
  } catch { /* ignore */ }
  // Strip leading + / digits and pass through anything else.
  return e164.replace(/^\+\d+/, '').trim()
}

// Public convenience for validation checks.
export function isValidPhoneE164(input, country = 'US') {
  if (!input) return false
  const parsed = parsePhoneNumberFromString(input, country)
  return !!parsed?.isValid()
}

export function toE164(input, country = 'US') {
  if (!input) return ''
  const parsed = parsePhoneNumberFromString(input, country)
  return parsed?.isValid() ? parsed.number : input
}
